import React, { useEffect, useState } from "react";
import { Check, X } from "lucide-react";

const APP_TOKENS = ["employeesfirst", "employees first", "myemployeesfirst", "prevue"];

export function passwordRules(password, { confirm, personal = [] } = {}) {
  const value = password || "";
  const lowered = value.toLowerCase();
  const flat = lowered.replace(/\s+/g, "");
  const fragments = [];
  personal.filter(Boolean).forEach((part) => {
    fragments.push(part);
    if (part.includes("@")) {
      const [local, domain] = part.split("@");
      fragments.push(local, (domain || "").split(".")[0]);
    }
  });

  const rules = [
    { key: "length", label: "12 to 128 characters", ok: value.length >= 12 && value.length <= 128 },
    { key: "upper", label: "An uppercase letter", ok: /[A-Z]/.test(value) },
    { key: "lower", label: "A lowercase letter", ok: /[a-z]/.test(value) },
    { key: "digit", label: "A number", ok: /[0-9]/.test(value) },
    { key: "symbol", label: "A symbol", ok: /[^A-Za-z0-9]/.test(value) },
    {
      key: "personal",
      label: "No part of your name, username or email",
      ok: !fragments.some((f) => {
        const frag = String(f).trim().toLowerCase();
        return frag.length >= 4 && lowered.includes(frag);
      }),
    },
    {
      key: "appname",
      label: "Does not contain the portal name",
      ok: !APP_TOKENS.some((t) => lowered.includes(t) || flat.includes(t.replace(/\s/g, ""))),
    },
  ];

  if (confirm !== undefined) {
    rules.push({
      key: "match",
      label: "Both passwords match",
      ok: value.length > 0 && value === confirm,
    });
  }
  return rules;
}

export function isPasswordValid(password, options) {
  return (password || "").length > 0 && passwordRules(password, options).every((r) => r.ok);
}

const STRENGTH_TIERS = [
  { label: "Very weak", color: "#B42318" },
  { label: "Weak", color: "#D9540D" },
  { label: "Fair", color: "#C79009" },
  { label: "Strong", color: "#15803D" },
  { label: "Very strong", color: "#0E6B36" },
];

const COMMON_FRAGMENTS = ["password", "qwerty", "welcome", "letmein", "admin", "login",
  "123456", "abc123", "iloveyou", "monkey", "dragon", "insurance", "captive"];

export function passwordStrength(password) {
  const value = password || "";
  if (!value) return { score: 0, ...STRENGTH_TIERS[0] };

  let score = 0;
  if (value.length >= 8) score += 1;
  if (value.length >= 12) score += 1;
  if (value.length >= 16) score += 1;
  if (value.length >= 20) score += 1;

  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(value)).length;
  score += classes - 1;
  if (new Set(value).size >= 10) score += 1;

  const lowered = value.toLowerCase();
  if (COMMON_FRAGMENTS.some((f) => lowered.includes(f))) score -= 3;
  if (/(.)\1{2,}/.test(value)) score -= 1;
  if (/^[A-Za-z]+$/.test(value) || /^[0-9]+$/.test(value)) score -= 1;
  if (/(abc|bcd|cde|012|123|234|345|456|567|678|789)/.test(lowered)) score -= 1;

  const tier = Math.max(0, Math.min(4, Math.round(((Math.max(score, 0)) / 9) * 4)));
  return { score: tier, ...STRENGTH_TIERS[tier] };
}

export const PasswordStrengthBar = ({ password, testId = "password-strength" }) => {
  if (!password) return null;
  const { score, label, color } = passwordStrength(password);
  return (
    <div data-testid={testId} data-strength={label}>
      <div className="mb-2 flex items-center justify-between">
        <div className="eyebrow">Strength</div>
        <div className="text-xs font-bold" style={{ color }} data-testid={`${testId}-label`}>
          {label}
        </div>
      </div>
      <div className="flex gap-1.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-1.5 flex-1 transition-colors duration-300"
            style={{ background: i <= score ? color : "var(--border)" }}
          />
        ))}
      </div>
    </div>
  );
};

export const PasswordChecklist = ({ password, confirm, personal, testId = "password-checklist" }) => {
  const rules = passwordRules(password || "", { confirm, personal });
  const allMet = Boolean(password) && rules.every((r) => r.ok);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!allMet) {
      setDismissed(false);
      return;
    }
    const timer = setTimeout(() => setDismissed(true), 2000);
    return () => clearTimeout(timer);
  }, [allMet]);

  if (!password || dismissed) return null;
  const met = rules.filter((r) => r.ok).length;

  return (
    <div
      className="mt-3 border p-4"
      style={{ borderColor: "var(--border)", background: "var(--surface)" }}
      data-testid={testId}
    >
      <PasswordStrengthBar password={password} testId={`${testId}-strength`} />
      <div
        className="my-4 border-t"
        style={{ borderColor: "var(--border)" }}
      />
      <div className="mb-3 flex items-center justify-between">
        <div className="eyebrow">Password requirements</div>
        <div
          className="text-xs font-bold tabular-nums"
          style={{ color: met === rules.length ? "#15803D" : "var(--muted)" }}
          data-testid={`${testId}-progress`}
        >
          {met} / {rules.length}
        </div>
      </div>
      <ul className="space-y-2">
        {rules.map((rule) => (
          <li
            key={rule.key}
            className="flex items-center gap-2 text-sm transition-colors"
            style={{ color: rule.ok ? "#15803D" : "#B42318" }}
            data-testid={`${testId}-${rule.key}-${rule.ok ? "met" : "unmet"}`}
          >
            {rule.ok ? <Check size={15} strokeWidth={3} /> : <X size={15} strokeWidth={3} />}
            <span style={{ color: rule.ok ? "var(--ink)" : "#B42318" }}>{rule.label}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs" style={{ color: "var(--muted)" }}>
        Passwords found in a known data breach are rejected when you save.
      </p>
    </div>
  );
};

export default PasswordChecklist;
